import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ManageAccounts as ManageIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

const formatDate = (date) => {
  if (!date) return 'N/A';
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const AgentListView = ({ agents, onDelete }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleRowClick = (agentId) => {
    navigate(`/agents/${agentId}/manage`);
  };

  const handleManageClick = (e, agentId) => {
    e.stopPropagation();
    navigate(`/agents/${agentId}/manage`);
  };

  const handleDeleteClick = (e, agent) => {
    e.stopPropagation();
    onDelete(agent);
  };

  return (
    <TableContainer component={Paper} sx={{ backgroundColor: theme.palette.background.paper }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Agent</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Agent ID</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Endpoint</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Created</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Last Updated</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {agents.map((agent) => (
            <TableRow 
              key={agent.id}
              sx={{ 
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.02)' },
                cursor: 'pointer'
              }}
              onClick={() => handleRowClick(agent.id)}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: theme.palette.primary.main, 
                    width: 40, 
                    height: 40,
                    fontSize: '1rem',
                    fontWeight: 600
                  }}>
                    {agent.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                      {agent.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      AI Agent
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={agent.isActive ? 'ACTIVE' : 'INACTIVE'}
                  color={agent.isActive ? 'success' : 'warning'}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    bgcolor: agent.isActive ? theme.palette.primary.main : '#ff9800',
                    color: agent.isActive ? 'black' : 'white'
                  }}
                />
              </TableCell>
              <TableCell sx={{ color: 'white' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {agent.id}
                </Typography>
              </TableCell>
              <TableCell sx={{ color: 'white' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {agent.endpoint}
                </Typography>
              </TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <Typography variant="body2">
                  {formatDate(agent.createdAt)}
                </Typography>
              </TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <Typography variant="body2">
                  {formatDate(agent.updatedAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Manage Agent">
                    <IconButton
                      size="small"
                      onClick={(e) => handleManageClick(e, agent.id)}
                      sx={{ 
                        color: theme.palette.primary.main,
                        '&:hover': { backgroundColor: 'rgba(64, 196, 99, 0.1)' }
                      }}
                    >
                      <ManageIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Agent">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleDeleteClick(e, agent)}
                      sx={{ 
                        '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

AgentListView.propTypes = {
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      endpoint: PropTypes.string.isRequired,
      isActive: PropTypes.bool.isRequired,
      createdAt: PropTypes.string.isRequired,
      updatedAt: PropTypes.string.isRequired
    })
  ).isRequired,
  onDelete: PropTypes.func.isRequired
};

export default AgentListView; 